package com.thanawyamarathon

import react.FC
import react.Props
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.footer
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.header
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.details
import react.dom.html.ReactHTML.summary
import react.useState
import web.cssom.ClassName
import web.cssom.pct
import web.cssom.px
import web.cssom.url
import web.dom.ElementId
import react.Key
import web.html.InputType
import web.html.ButtonType
// import js.objects.jso (Removing due to unresolved reference)

private fun <T : Any> jso(block: T.() -> Unit): T =
    js("{}").unsafeCast<T>().apply(block)

val ReactApp = FC<Props> {
    div {
        className = ClassName("min-h-screen bg-navy-deep text-white")
        
        Nav {}
        main {
            Hero {}
            Features {}
            HowItWorks {}
            FreeBanner {}
            FAQ {}
            FinalCTA {}
        }
        Footer {}
    }
}

// Icon Component using Official SVG Resources
external interface LucideIconProps : Props {
    var name: String
    var className: String?
}

val LucideIcon = FC<LucideIconProps> { props ->
    div {
        className = ClassName("${props.className ?: "h-5 w-5"} bg-current")
        style = jso {
            val iconUrl = "url('./icons/${props.name}.svg')"
            asDynamic()["maskImage"] = iconUrl
            asDynamic()["webkitMaskImage"] = iconUrl
            asDynamic()["maskSize"] = "contain"
            asDynamic()["webkitMaskSize"] = "contain"
            asDynamic()["maskRepeat"] = "no-repeat"
            asDynamic()["webkitMaskRepeat"] = "no-repeat"
            asDynamic()["maskPosition"] = "center"
            asDynamic()["webkitMaskPosition"] = "center"
        }
    }
}

// Brand Logo Replicating App.kt Design
val BrandLogo = FC<Props> {
    div {
        className = ClassName("flex items-center gap-3")
        img {
            src = "./logo.jpeg"
            alt = "شعار ثانوية ماراثون"
            className = ClassName("h-10 w-10 rounded-full object-cover ring-2 ring-gold/60 shadow-gold")
        }
        div {
            className = ClassName("hidden sm:block")
            div {
                className = ClassName("font-display text-base font-black text-white")
                +"ثانوية ماراثون"
            }
            div {
                className = ClassName("text-[10px] text-gold-soft font-bold")
                +"قريباً"
            }
        }
    }
}

val Nav = FC<Props> {
    header {
        className = ClassName("sticky top-0 z-50 border-b border-white/5 bg-navy-deep/80 backdrop-blur-xl")
        div {
            className = ClassName("mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8")
            
            a {
                href = "#"
                BrandLogo {}
            }
            
            nav {
                className = ClassName("hidden items-center gap-1 md:flex")
                listOf("المميزات" to "#features", "إزاي بيشتغل" to "#how", "الأسئلة" to "#faq").forEach { (label, hrefValue) ->
                    a {
                        key = Key(hrefValue)
                        href = hrefValue
                        className = ClassName("rounded-lg px-3 py-2 text-sm text-white/70 transition hover:bg-white/5 hover:text-white")
                        +label
                    }
                }
            }
            
            a {
                href = "#signup"
                className = ClassName("inline-flex items-center gap-1 rounded-lg bg-gold-gradient px-4 py-2 text-sm font-bold text-navy-deep shadow-gold transition hover:brightness-105")
                +"سجّل مبكراً"
                LucideIcon {
                    name = "chevron-left"
                    className = "h-4 w-4"
                }
            }
        }
    }
}

val Hero = FC<Props> {
    section {
        className = ClassName("relative overflow-hidden bg-hero")
        div { className = ClassName("absolute inset-0 grid-bg opacity-40") }
        div { className = ClassName("absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-gold/20 blur-3xl") }
        
        div {
            className = ClassName("relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:py-24 lg:px-8")
            
            div {
                className = ClassName("flex flex-col justify-center")
                div {
                    className = ClassName("inline-flex w-fit items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs font-bold text-gold-soft")
                    LucideIcon {
                        name = "sparkles"
                        className = "h-3.5 w-3.5"
                    }
                    +"قريباً • التسجيل المبكر مفتوح"
                }
                h1 {
                    className = ClassName("mt-5 font-display text-4xl leading-tight font-black text-white sm:text-5xl lg:text-6xl")
                    span { className = ClassName("inline-block"); +"🏁" }
                    +" ثانوية "
                    span { className = ClassName("text-gradient-gold"); +"ماراثون" }
                }
                p {
                    className = ClassName("mt-5 max-w-xl text-lg text-white/80 sm:text-xl")
                    +"أول منصة مذاكرة تنافسية لطلاب الثانوية المصرية."
                }
                p {
                    className = ClassName("mt-3 text-xl font-bold text-gold-soft sm:text-2xl")
                    +"ذاكر... نافس... وطوّر مستواك 🚀"
                }
                
                div {
                    id = ElementId("signup")
                    className = ClassName("mt-8 max-w-lg")
                    SignupForm {}
                    div {
                        className = ClassName("mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/60")
                        span { 
                            className = ClassName("inline-flex items-center gap-1")
                            LucideIcon { name = "gift"; className = "h-3.5 w-3.5 text-gold" }
                            +"مجانية دائماً" 
                        }
                        span { 
                            className = ClassName("inline-flex items-center gap-1")
                            LucideIcon { name = "shield-check"; className = "h-3.5 w-3.5 text-gold" }
                            +"بدون التزام" 
                        }
                        span { 
                            className = ClassName("inline-flex items-center gap-1")
                            LucideIcon { name = "flame"; className = "h-3.5 w-3.5 text-gold" }
                            +"أولوية للمسجلين مبكراً" 
                        }
                    }
                }
            }
            
            HeroIllustration {}
        }
    }
}

val HeroIllustration = FC<Props> {
    div {
        className = ClassName("relative animate-float")
        div { className = ClassName("absolute -inset-6 rounded-[2rem] bg-gold/10 blur-3xl") }
        div {
            className = ClassName("relative rounded-[1.75rem] border border-white/10 bg-card-gradient p-6 shadow-navy sm:p-8")
            div {
                className = ClassName("flex items-center justify-between")
                div {
                    className = ClassName("flex items-center gap-2")
                    span { className = ClassName("h-2.5 w-2.5 rounded-full bg-gold animate-pulse") }
                    span { className = ClassName("text-xs font-bold text-white/80"); +"نظرة على المنصة" }
                }
                span {
                    className = ClassName("rounded-full bg-gold/15 px-2.5 py-1 text-[10px] font-bold text-gold-soft")
                    +"PREVIEW"
                }
            }
            
            div {
                className = ClassName("mt-8 flex flex-col items-center text-center")
                div {
                    className = ClassName("relative")
                    div { className = ClassName("absolute inset-0 rounded-full bg-gold/30 blur-2xl") }
                    div {
                        className = ClassName("relative grid h-28 w-28 place-items-center rounded-full bg-gold-gradient shadow-gold")
                        LucideIcon {
                            name = "trophy"
                            className = "h-14 w-14 text-navy-deep"
                        }
                    }
                }
                div {
                    className = ClassName("mt-6 font-display text-2xl font-black text-white")
                    +"بطولة المذاكرة"
                }
                p {
                    className = ClassName("mt-1 max-w-xs text-sm text-white/60")
                    +"تحديات، مواسم، ترتيب على مستوى الجمهورية"
                }
            }
            
            div {
                className = ClassName("mt-8 grid grid-cols-2 gap-3")
                listOf("zap" to "نقاط XP", "medal" to "شارات", "swords" to "تحديات", "crown" to "ترتيب").forEach { (iconName, label) ->
                    div {
                        key = Key(label)
                        className = ClassName("flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3")
                        div {
                            className = ClassName("grid h-9 w-9 place-items-center rounded-lg bg-gold-gradient text-navy-deep shadow-gold")
                            LucideIcon {
                                name = iconName
                                className = "h-4 w-4"
                            }
                        }
                        span { className = ClassName("text-sm font-bold text-white"); +label }
                    }
                }
            }
        }
    }
}

val SignupForm = FC<Props> {
    var email by useState("")
    var submitted by useState(false)
    
    if (submitted) {
        div {
            className = ClassName("flex items-center gap-3 rounded-2xl border border-success/40 bg-success/10 p-4 text-sm animate-in fade-in zoom-in duration-300")
            LucideIcon { name = "check-circle-2"; className = "h-5 w-5 shrink-0 text-success" }
            div {
                div { className = ClassName("font-bold text-white"); +"تمام! اسمك اتسجل." }
                div { className = ClassName("text-white/70"); +"هنبعتلك أول ما المنصة تفتح." }
            }
        }
    } else {
        form {
            className = ClassName("flex flex-col gap-2 sm:flex-row")
            onSubmit = { e ->
                e.preventDefault()
                if (email.isNotBlank()) submitted = true
            }
            div {
                className = ClassName("relative flex-1")
                LucideIcon { name = "mail"; className = "pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" }
                input {
                    type = "email".unsafeCast<InputType>()
                    required = true
                    value = email
                    onChange = { e -> email = e.target.value }
                    placeholder = "ايميلك أو رقم واتساب"
                    className = ClassName("w-full rounded-xl border border-white/15 bg-white/5 py-3.5 pr-10 pl-4 text-sm text-white placeholder:text-white/40 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/30 transition-all")
                }
            }
            button {
                type = "submit".unsafeCast<ButtonType>()
                className = ClassName("inline-flex items-center justify-center gap-2 rounded-xl bg-gold-gradient px-6 py-3.5 text-sm font-black text-navy-deep shadow-gold transition hover:brightness-105 active:scale-95")
                LucideIcon { name = "rocket"; className = "h-4 w-4" }
                +"احجز مكاني"
            }
        }
    }
}

val Features = FC<Props> {
    section {
        id = ElementId("features")
        className = ClassName("relative py-20 sm:py-28")
        div {
            className = ClassName("mx-auto max-w-7xl px-4 sm:px-6 lg:px-8")
            SectionHeader {
                eyebrow = "إيه اللي هيبقى فيها"
                title = "مش دروس خصوصية. دي بطولة."
                desc = "منصة مصممة عشان تحس إنك في ملعب مش في فصل. كل مذاكرة عندها معنى."
            }
            
            div {
                className = ClassName("mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3")
                listOf(
                    Triple("swords", "تحديات ومواسم", "تحديات أسبوعية ومواسم على مدار السنة. كل تحدي فرصة تكسب فيه نقاط وترتفع في الترتيب."),
                    Triple("trophy", "ترتيب على مستوى مصر", "قارن نفسك بطلاب من كل المحافظات. ترتيب قومي وترتيب على مستوى محافظتك."),
                    Triple("brain", "تحليل ذكي لأدائك", "الذكاء الاصطناعي يحدد نقاط ضعفك ويرشحلك مواضيع محددة تركز عليها."),
                    Triple("trending-up", "تقارير تطورك", "شوف تطورك أسبوع ورا أسبوع بالأرقام والرسوم البيانية."),
                    Triple("target", "تدريب مخصص", "مهمات يومية على مقاس مستواك، تتغير حسب أدائك ونقاط ضعفك."),
                    Triple("medal", "شارات وإنجازات", "اجمع شارات على كل إنجاز، وابني بروفايل تنافسي حقيقي.")
                ).forEach { (iconName, title, descValue) ->
                    div {
                        key = Key(title)
                        className = ClassName("group relative overflow-hidden rounded-2xl border border-white/10 bg-card-gradient p-6 shadow-navy transition hover:-translate-y-1 hover:border-gold/40")
                        div { className = ClassName("absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold/10 blur-3xl transition group-hover:bg-gold/20") }
                        div {
                            className = ClassName("relative grid h-12 w-12 place-items-center rounded-xl bg-gold-gradient text-navy-deep shadow-gold")
                            LucideIcon {
                                name = iconName
                                className = "h-6 w-6"
                            }
                        }
                        h3 {
                            className = ClassName("relative mt-5 font-display text-xl font-black text-white")
                            +title
                        }
                        p {
                            className = ClassName("relative mt-2 text-sm text-white/70")
                            +descValue
                        }
                    }
                }
            }
        }
    }
}

val HowItWorks = FC<Props> {
    section {
        id = ElementId("how")
        className = ClassName("relative border-y border-white/5 bg-navy-deep py-20 sm:py-28")
        div { className = ClassName("absolute inset-0 grid-bg opacity-30") }
        div {
            className = ClassName("relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8")
            SectionHeader {
                eyebrow = "إزاي بيشتغل"
                title = "3 خطوات وتبقى في الملعب"
                desc = "من التسجيل لأول تحدي — بسيطة، سريعة، ومجانية."
            }
            
            div {
                className = ClassName("mt-14 grid gap-6 md:grid-cols-3")
                listOf(
                    Triple("01", "mail", "سجّل مبكراً" to "اكتب ايميلك أو رقمك عشان نبعتلك أول ما المنصة تفتح."),
                    Triple("02", "rocket", "ادخل الملعب" to "افتح حسابك، اختار مادتك، وابدأ التحدي الأول."),
                    Triple("03", "crown", "نافس واكسب" to "اجمع XP، افتح شارات، وارتقي في ترتيب طلاب مصر.")
                ).forEachIndexed { index, (num, iconName, data) ->
                    div {
                        key = Key(num)
                        className = ClassName("relative")
                        div {
                            className = ClassName("rounded-2xl border border-white/10 bg-card-gradient p-6 shadow-navy h-full")
                            div {
                                className = ClassName("flex items-center justify-between")
                                span { className = ClassName("font-display text-4xl font-black text-white/10"); +num }
                                div {
                                    className = ClassName("grid h-12 w-12 place-items-center rounded-xl bg-gold-gradient text-navy-deep shadow-gold")
                                    LucideIcon {
                                        name = iconName
                                        className = "h-6 w-6"
                                    }
                                }
                            }
                            h3 { className = ClassName("mt-4 font-display text-xl font-black text-white"); +data.first }
                            p { className = ClassName("mt-2 text-sm text-white/70"); +data.second }
                        }
                        if (index < 2) {
                            LucideIcon {
                                name = "chevron-left"
                                className = "absolute top-1/2 -left-3 hidden h-6 w-6 -translate-y-1/2 text-gold/50 md:block"
                            }
                        }
                    }
                }
            }
        }
    }
}

val FreeBanner = FC<Props> {
    section {
        className = ClassName("py-20")
        div {
            className = ClassName("mx-auto max-w-5xl px-4 sm:px-6 lg:px-8")
            div {
                className = ClassName("relative overflow-hidden rounded-3xl border border-gold/30 bg-card-gradient p-10 shadow-navy sm:p-14")
                div { className = ClassName("absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gold/20 blur-3xl") }
                div {
                    className = ClassName("relative grid items-center gap-6 md:grid-cols-[auto_1fr]")
                    div {
                        className = ClassName("grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-gold-gradient text-navy-deep shadow-gold")
                        LucideIcon { name = "gift"; className = "h-10 w-10" }
                    }
                    div {
                        h2 {
                            className = ClassName("font-display text-3xl font-black text-white sm:text-4xl")
                            +"مجانية "
                            span { className = ClassName("text-gradient-gold"); +"دائماً" }
                        }
                        p {
                            className = ClassName("mt-3 max-w-xl text-white/75")
                            +"ثانوية ماراثون هتفضل مجانية للطلاب طول عمرها. من غير اشتراكات، من غير خطط مدفوعة، من غير خدع. هدفنا نساعد كل طالب مصري ينافس."
                        }
                    }
                }
            }
        }
    }
}

val FAQ = FC<Props> {
    section {
        id = ElementId("faq")
        className = ClassName("relative py-20 sm:py-28")
        div {
            className = ClassName("mx-auto max-w-3xl px-4 sm:px-6 lg:px-8")
            SectionHeader {
                eyebrow = "أسئلة شائعة"
                title = "أي حاجة محتاج تعرفها"
                desc = ""
            }
            div {
                className = ClassName("mt-12 space-y-3")
                listOf(
                    "المنصة متاحة دلوقتي؟" to "لسه لأ. احنا في مرحلة التسجيل المبكر. سجّل بايميلك عشان تبقى أول ناس تعرف لما نفتح.",
                    "هل هدفع أي حاجة؟" to "لا. المنصة مجانية دائماً للطلاب. مفيش اشتراكات ولا خطط مدفوعة.",
                    "لمين المنصة؟" to "لطلاب المرحلة الثانوية في مصر — أولى وتانية وتالتة ثانوي.",
                    "إيه اللي هستفيده كمسجّل مبكر؟" to "أولوية في الدخول لأول موسم، وشارات خاصة بالمؤسسين لما نفتح."
                ).forEach { (q, aValue) ->
                    details {
                        key = Key(q)
                        className = ClassName("group rounded-2xl border border-white/10 bg-card-gradient p-5 shadow-navy [&_summary::-webkit-details-marker]:hidden transition-all duration-300")
                        summary {
                            className = ClassName("flex cursor-pointer items-center justify-between gap-4 font-display font-black text-white list-none")
                            +q
                            LucideIcon { 
                                name = "chevron-left"
                                className = "h-5 w-5 text-gold transition-transform group-open:-rotate-90"
                            }
                        }
                        p { className = ClassName("mt-3 text-sm text-white/70 animate-in fade-in slide-in-from-top-2 duration-300"); +aValue }
                    }
                }
            }
        }
    }
}

val FinalCTA = FC<Props> {
    section {
        className = ClassName("relative py-20 sm:py-28")
        div {
            className = ClassName("mx-auto max-w-5xl px-4 sm:px-6 lg:px-8")
            div {
                className = ClassName("relative overflow-hidden rounded-3xl border border-gold/30 bg-card-gradient p-10 text-center shadow-navy sm:p-16")
                div { className = ClassName("absolute inset-0 grid-bg opacity-30") }
                div { className = ClassName("absolute -top-32 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-gold/25 blur-3xl") }
                div {
                    className = ClassName("relative")
                    div {
                        className = ClassName("mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gold-gradient text-navy-deep shadow-gold")
                        LucideIcon { name = "rocket"; className = "h-8 w-8" }
                    }
                    h2 {
                        className = ClassName("mt-6 font-display text-3xl font-black text-white sm:text-5xl")
                        +"جاهز تكون أول واحد في "
                        span { className = ClassName("text-gradient-gold"); +"الملعب" }
                        +"؟"
                    }
                    p {
                        className = ClassName("mx-auto mt-4 max-w-xl text-white/75")
                        +"سجّل مبكراً وهنبعتلك أول ما ثانوية ماراثون تفتح. مجاناً، دايماً."
                    }
                    div {
                        className = ClassName("mx-auto mt-8 max-w-md")
                        SignupForm {}
                    }
                    p {
                        className = ClassName("mt-4 text-xs text-white/50")
                        LucideIcon { name = "timer"; className = "me-1 inline h-3.5 w-3.5" }
                        +"مكانك محجوز لحد ما نفتح"
                    }
                }
            }
        }
    }
}

val Footer = FC<Props> {
    footer {
        className = ClassName("border-t border-white/5 bg-navy-deep py-12")
        div {
            className = ClassName("mx-auto max-w-7xl px-4 sm:px-6 lg:px-8")
            div {
                className = ClassName("flex flex-col items-center gap-4 text-center")
                a {
                    href = "#"
                    BrandLogo {}
                }
                p {
                    className = ClassName("mt-4 max-w-md text-sm text-white/60")
                    +"منصة مذاكرة تنافسية مجانية لطلاب الثانوية المصرية."
                }
                div {
                    className = ClassName("mt-6 flex flex-col items-center gap-1 border-t border-white/5 pt-6 text-xs text-white/50 sm:flex-row sm:gap-4")
                    span { +"© 2026 ثانوية ماراثون" }
                    span { className = ClassName("hidden sm:inline"); +"•" }
                    span { +"صُنعت بشغف في مصر 🇪🇬" }
                }
            }
        }
    }
}

external interface SectionHeaderProps : Props {
    var eyebrow: String
    var title: String
    var desc: String
}

val SectionHeader = FC<SectionHeaderProps> { props ->
    div {
        className = ClassName("mx-auto max-w-2xl text-center")
        div {
            className = ClassName("inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs font-bold text-gold-soft")
            +props.eyebrow
        }
        h2 {
            className = ClassName("mt-4 font-display text-3xl font-black text-white sm:text-4xl")
            +props.title
        }
        p { className = ClassName("mt-3 text-white/70"); +props.desc }
    }
}
